import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { Awareness } from 'y-protocols/awareness';
import type { SceneStore } from '@os/doc';
import type { PresenceUser } from '@os/shared';
import type { BoardPerson } from '../../lib/sharing-types';
import type { EditorController } from '../editor/controller';
import { InTopBar } from '../editor/TopBar';
import { MembersPanel } from '../members/MembersPanel';
import { mergePeople } from '../members/rules';
import { AvatarStack } from './AvatarStack';
import { FollowBanner } from './FollowBanner';
import { RemoteCursors } from './RemoteCursors';
import { RemoteSelections } from './RemoteSelections';
import { onPage, peerPage, usePresence } from './usePresence';
import { useActivePage } from '../pages/usePageNav';
import { pageLabel } from '../pages/labels';
import { useTranslation } from 'react-i18next';

/**
 * `people`: who has access (members panel, P16-3); `onShare`: absent for whoever may not share; `economy`: the room's
 * quota mode at 70 % (P12), cursors at 2 Hz and no viewport for followers; `off`: from 90 %, nothing published nor
 * drawn, the members panel stays.
 */
export type PresenceProps = { awareness: Awareness; me: PresenceUser; people?: () => Promise<BoardPerson[]>; onShare?: () => void; economy?: boolean; off?: boolean };

/** Peers on the board: avatars (members panel, follow), their selections and cursors over the canvas. */
export function PresenceLayer({ awareness, me, people, onShare, economy, off, controller, store }: PresenceProps & { controller: EditorController; store: SceneStore }) {
  const { t } = useTranslation();
  const page = useMemo(() => ({ current: () => store.page, subscribe: (cb: () => void) => store.subscribePage(cb), set: (id: string) => store.setPage(id) }), [store]);
  const { peers, following, follow, followers, stoppedBy, clearStopped } = usePresence(awareness, controller, me, { page, economy, off });
  const myPage = useActivePage(store);
  // Cursors and selections of the people on my page only (P14 Ruling 15).
  const here = onPage(peers, store);
  const followed = following ? (peers.find((p) => p.user.id === following)?.user ?? null) : null;
  const camera = useSyncExternalStore(controller.subscribeCamera, () => controller.camera);
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<{ people: BoardPerson[]; status: 'loading' | 'ready' | 'error' }>({ people: [], status: 'loading' });
  const button = useRef<HTMLButtonElement>(null);
  const loaded = useRef(false);
  // Loaded with the board (the stack's "+N") and again each time the panel opens (access may have changed).
  // Read through a ref: the route builds `people` anew on each of its renders.
  const load = useRef(people);
  load.current = people;
  const canLoad = !!people;
  useEffect(() => {
    if (!canLoad || (!open && loaded.current)) return;
    let live = true;
    load.current!().then(
      (p) => {
        if (!live) return;
        loaded.current = true; // set on success: StrictMode's second run must still load
        setList({ people: p, status: 'ready' });
      },
      () => live && setList((l) => ({ ...l, status: 'error' })),
    );
    return () => {
      live = false;
    };
  }, [canLoad, open]);
  const rows = useMemo(() => {
    const pages = store.pages();
    const where = new Map(peers.map((p) => [p.user.id, peerPage(p, store)] as const));
    return mergePeople(list.people, peers, me.id, me.a ?? null).map((r) => {
      const at = where.get(r.userId);
      if (r.you || !at || at === myPage || pages.length < 2) return r;
      const i = pages.findIndex((p) => p.id === at);
      return { ...r, elsewhere: pageLabel(t, pages[i]!, i) };
    });
  }, [list.people, peers, me.id, me.a, store, myPage, t]);
  const stack = (docked: boolean) => (
    <AvatarStack rows={rows} me={{ name: me.n, color: me.c }} following={following} open={open} onToggle={() => setOpen((o) => !o)} docked={docked} buttonRef={button} />
  );
  return (
    <>
      <RemoteSelections peers={here} store={store} camera={camera} />
      <RemoteCursors peers={here} camera={camera} />
      <InTopBar slot="presence" fallback={stack(false)}>
        {stack(true)}
      </InTopBar>
      {open && (
        <MembersPanel
          rows={rows}
          status={people ? list.status : 'ready'}
          following={following}
          onFollow={follow}
          onShare={
            onShare &&
            (() => {
              setOpen(false);
              onShare();
            })
          }
          onClose={() => setOpen(false)}
          toggle={button}
        />
      )}
      <FollowBanner followed={followed} followers={followers} stoppedBy={stoppedBy} onStop={() => follow(null)} onToastDone={clearStopped} />
    </>
  );
}
